import React, { useState } from "react";
import { useSitemap } from "../utils/sitemapGenerator";
import { useFetch } from "../hooks/useFetch";
import { apiEndpoints } from "../utils/api";

export const SEOAdmin = () => {
  const [seoAudit, setSeoAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const { generateAndDownloadSitemap, generateAndDownloadRobots } =
    useSitemap();

  // Cargar datos para auditoría
  const { data: settings } = useFetch(apiEndpoints.settings.public());
  const { data: posts } = useFetch(apiEndpoints.posts.list(1, 50));

  const runSEOAudit = async () => {
    setLoading(true);

    // Simular auditoría SEO
    const audit = {
      timestamp: new Date().toISOString(),
      pages: {
        total: posts?.data?.length || 0,
        withMetaDescription:
          posts?.data?.filter((p) => p.description || p.excerpt)?.length || 0,
        withImages:
          posts?.data?.filter((p) => p.featured_image || p.image)?.length || 0,
        withTags:
          posts?.data?.filter((p) => p.tags && p.tags.length > 0)?.length || 0,
      },
      siteSettings: {
        hasTitle: !!settings?.site?.name,
        hasDescription: !!settings?.site?.description,
        hasLogo: !!settings?.site?.logo,
        hasSocialMedia: !!(
          settings?.social?.facebook || settings?.social?.twitter
        ),
      },
      recommendations: [],
    };

    // Generar recomendaciones
    if (!audit.siteSettings.hasTitle) {
      audit.recommendations.push("Configurar título del sitio");
    }
    if (!audit.siteSettings.hasDescription) {
      audit.recommendations.push("Agregar descripción del sitio");
    }
    if (audit.pages.withMetaDescription < audit.pages.total * 0.8) {
      audit.recommendations.push("Agregar meta descripciones a más posts");
    }
    if (audit.pages.withImages < audit.pages.total * 0.9) {
      audit.recommendations.push("Agregar imágenes destacadas a más posts");
    }

    setSeoAudit(audit);
    setLoading(false);
  };

  const seoScore = seoAudit
    ? Math.round(
        (seoAudit.siteSettings.hasTitle ? 25 : 0) +
          (seoAudit.siteSettings.hasDescription ? 25 : 0) +
          (seoAudit.pages.withMetaDescription / seoAudit.pages.total) * 25 +
          (seoAudit.pages.withImages / seoAudit.pages.total) * 25
      )
    : 0;

  return (
    <div className="seo-admin">
      <style jsx>{`
        .seo-admin {
          max-width: 1000px;
          margin: 0 auto;
          padding: 20px;
          background: #f8fafc;
          min-height: 100vh;
        }

        .admin-header {
          background: white;
          padding: 30px;
          border-radius: 12px;
          margin-bottom: 30px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        .admin-title {
          font-size: 2rem;
          font-weight: bold;
          color: #1f2937;
          margin-bottom: 10px;
        }

        .admin-description {
          color: #6b7280;
          font-size: 1.1rem;
        }

        .admin-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .admin-card {
          background: white;
          padding: 24px;
          border-radius: 12px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        .card-title {
          font-size: 1.25rem;
          font-weight: bold;
          color: #1f2937;
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .action-button {
          background: #3b82f6;
          color: white;
          border: none;
          padding: 12px 20px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          transition: background 0.3s ease;
          width: 100%;
          margin-bottom: 10px;
        }

        .action-button:hover {
          background: #2563eb;
        }

        .action-button:disabled {
          background: #d1d5db;
          cursor: not-allowed;
        }

        .secondary-button {
          background: #10b981;
        }

        .secondary-button:hover {
          background: #059669;
        }

        .audit-results {
          background: white;
          padding: 30px;
          border-radius: 12px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        .seo-score {
          text-align: center;
          margin-bottom: 30px;
        }

        .score-circle {
          width: 120px;
          height: 120px;
          border-radius: 50%;
          margin: 0 auto 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          font-weight: bold;
          color: white;
        }

        .score-excellent {
          background: #10b981;
        }
        .score-good {
          background: #f59e0b;
        }
        .score-poor {
          background: #ef4444;
        }

        .audit-section {
          margin-bottom: 24px;
        }

        .section-title {
          font-size: 1.1rem;
          font-weight: bold;
          color: #1f2937;
          margin-bottom: 12px;
        }

        .audit-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid #f3f4f6;
        }

        .recommendation {
          background: #fef3c7;
          border: 1px solid #f59e0b;
          padding: 12px;
          border-radius: 6px;
          margin-bottom: 8px;
          color: #92400e;
        }

        .status-good {
          color: #10b981;
        }
        .status-warning {
          color: #f59e0b;
        }
        .status-error {
          color: #ef4444;
        }

        @media (max-width: 768px) {
          .admin-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="admin-header">
        <h1 className="admin-title">Panel de Administración SEO</h1>
        <p className="admin-description">
          Gestiona y optimiza el SEO de Red Arauca
        </p>
      </div>

      <div className="admin-grid">
        {/* Herramientas de generación */}
        <div className="admin-card">
          <h2 className="card-title">🗺️ Herramientas de SEO</h2>
          <button
            className="action-button"
            onClick={generateAndDownloadSitemap}
          >
            Generar Sitemap.xml
          </button>
          <button
            className="action-button secondary-button"
            onClick={generateAndDownloadRobots}
          >
            Generar Robots.txt
          </button>
          <p
            style={{ fontSize: "0.9rem", color: "#6b7280", marginTop: "12px" }}
          >
            Descarga archivos esenciales para motores de búsqueda
          </p>
        </div>

        {/* Auditoría SEO */}
        <div className="admin-card">
          <h2 className="card-title">🔍 Auditoría SEO</h2>
          <button
            className="action-button"
            onClick={runSEOAudit}
            disabled={loading}
          >
            {loading ? "Ejecutando..." : "Ejecutar Auditoría"}
          </button>
          <p
            style={{ fontSize: "0.9rem", color: "#6b7280", marginTop: "12px" }}
          >
            Analiza el estado actual del SEO del sitio
          </p>
        </div>

        {/* Estadísticas rápidas */}
        <div className="admin-card">
          <h2 className="card-title">📊 Estadísticas</h2>
          <div className="audit-item">
            <span>Posts totales:</span>
            <span className="status-good">{posts?.data?.length || 0}</span>
          </div>
          <div className="audit-item">
            <span>Configuración del sitio:</span>
            <span className={settings ? "status-good" : "status-warning"}>
              {settings ? "Configurado" : "Pendiente"}
            </span>
          </div>
        </div>
      </div>

      {/* Resultados de auditoría */}
      {seoAudit && (
        <div className="audit-results">
          <div className="seo-score">
            <div
              className={`score-circle ${
                seoScore >= 80
                  ? "score-excellent"
                  : seoScore >= 60
                  ? "score-good"
                  : "score-poor"
              }`}
            >
              {seoScore}%
            </div>
            <h3>Puntuación SEO</h3>
          </div>

          <div className="audit-section">
            <h3 className="section-title">Configuración del Sitio</h3>
            <div className="audit-item">
              <span>Título del sitio</span>
              <span
                className={
                  seoAudit.siteSettings.hasTitle
                    ? "status-good"
                    : "status-error"
                }
              >
                {seoAudit.siteSettings.hasTitle ? "✓" : "✗"}
              </span>
            </div>
            <div className="audit-item">
              <span>Descripción del sitio</span>
              <span
                className={
                  seoAudit.siteSettings.hasDescription
                    ? "status-good"
                    : "status-error"
                }
              >
                {seoAudit.siteSettings.hasDescription ? "✓" : "✗"}
              </span>
            </div>
            <div className="audit-item">
              <span>Logo del sitio</span>
              <span
                className={
                  seoAudit.siteSettings.hasLogo
                    ? "status-good"
                    : "status-warning"
                }
              >
                {seoAudit.siteSettings.hasLogo ? "✓" : "✗"}
              </span>
            </div>
          </div>

          <div className="audit-section">
            <h3 className="section-title">Contenido</h3>
            <div className="audit-item">
              <span>Posts con meta descripción</span>
              <span
                className={
                  seoAudit.pages.withMetaDescription / seoAudit.pages.total >
                  0.8
                    ? "status-good"
                    : "status-warning"
                }
              >
                {seoAudit.pages.withMetaDescription}/{seoAudit.pages.total}
              </span>
            </div>
            <div className="audit-item">
              <span>Posts con imágenes</span>
              <span
                className={
                  seoAudit.pages.withImages / seoAudit.pages.total > 0.9
                    ? "status-good"
                    : "status-warning"
                }
              >
                {seoAudit.pages.withImages}/{seoAudit.pages.total}
              </span>
            </div>
          </div>

          {seoAudit.recommendations.length > 0 && (
            <div className="audit-section">
              <h3 className="section-title">Recomendaciones</h3>
              {seoAudit.recommendations.map((rec, index) => (
                <div key={index} className="recommendation">
                  {rec}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
